// NOTE: AI provider and processing-library names are deliberately NOT listed
// here. They are scrubbed server-side by utils.error_sanitizer (the global DRF
// EXCEPTION_HANDLER), so they never reach the browser and must not be shipped
// in this bundle. What remains is a thin, vendor-neutral safety net for any
// generic technical text.
const TECHNICAL_PATTERNS = [
  /rate.?limit/i,
  /traceback|stacktrace|exception/i,
  /integrity.?error|duplicate.?key|unique.?constraint/i,
  /connection.?(refused|reset|timeout)/i,
  /ECONNREFUSED|ETIMEDOUT|ERR_NETWORK/i,
  /500 internal server/i,
  /out.?of.?memory|oom/i,
  /json\.?decode|json\.?loads/i,
  /permission.?denied|access.?denied/i,
  /module|import|attribute.?error|type.?error|key.?error/i,
  /\.py|\.js|line \d+/i,
  /\bat\b.*\(.*:\d+:\d+\)/,
];

/**
 * True when the error was thrown by code that wrapped a server error -- kept as
 * `cause`, with its response carried over -- and wrote its own message from
 * that answer. Its message is the text to show; the raw body is not.
 *
 * Ledger row 127: crmService keeps the server's answer on its friendlier
 * errors so screens can read the status. Without this, the body would win here
 * and "Validation failed: legal_entity_name: …" would read as "Validation
 * failed", and "CRM profile not found" as "Not found.".
 */
const composedFromServerAnswer = (error) =>
  Boolean(error?.cause) && error.cause.response === error.response;

/**
 * Extract a safe, user-friendly error message from an API error.
 * Second line of defense -- catches technical patterns the backend might leak.
 *
 * @param {Error|Object} error - Axios error or any error object.
 * @param {string} fallback - Context-specific user-friendly fallback message.
 * @returns {string} Safe message suitable for display.
 */
export function getErrorMessage(error, fallback = 'Something went wrong. Please try again.') {
  const data = composedFromServerAnswer(error) ? undefined : error?.response?.data;
  const raw = data?.error || data?.message || data?.detail || error?.message || '';

  if (!raw || typeof raw !== 'string') return fallback;
  if (TECHNICAL_PATTERNS.some((p) => p.test(raw))) return fallback;
  if (raw.length > 200) return fallback;

  return raw;
}

// Field names in the words the screens use. Anything not listed falls back to
// the API's own key, which is still better than dropping the field entirely.
const FIELD_LABELS = {
  bank_account: 'Bank account',
  cost_category: 'Cost category',
  cost_center: 'Cost centre',
  currency: 'Currency',
  due_date: 'Due date',
  gl_account: 'GL account',
  gross_amount: 'Gross amount',
  hms_entity: 'IMT entity',
  internal_reference: 'Internal reference',
  invoice_date: 'Invoice date',
  item_description: 'Description',
  line_number: 'Line number',
  net_amount: 'Net amount',
  partner: 'Partner',
  partner_name: 'Partner name',
  payment_terms: 'Payment terms',
  pledge_bank_key: 'Pledge bank',
  product: 'Product',
  quantity: 'Quantity',
  reporting_month: 'Reporting month',
  supplier_invoice_number: 'Supplier invoice number',
  template_type: 'Template type',
  unit: 'Unit',
  unit_price: 'Unit price',
  vat_amount: 'VAT amount',
  vat_rate: 'VAT rate',
  vessel_info: 'Vessel & shipment',
};

// Keys whose value is a per-row list, so an error at index 2 belongs to row 3.
const ROW_COLLECTIONS = { line_items: 'Line' };

const labelFor = (field) => FIELD_LABELS[field] || field;

// DRF hands back strings, arrays of strings, and (for a nested serializer)
// arrays of objects. Flatten whatever arrives to a sentence.
const asText = (value) => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(asText).filter(Boolean).join(', ');
  if (typeof value === 'object') return fieldsToText(value, '; ');
  return String(value);
};

const fieldsToText = (obj, separator) =>
  Object.entries(obj)
    .map(([field, value]) =>
      field === 'non_field_errors' ? asText(value) : `${labelFor(field)}: ${asText(value)}`
    )
    .filter(Boolean)
    .join(separator);

// Keys a 400 body may carry that are DATA for the screen rather than text for
// the user. They are skipped when composing the message; the screen reads them
// directly. `duplicate_invoice` points at the invoice already holding a supplier
// invoice number, so the screen can offer to open it.
const METADATA_FIELDS = new Set(['duplicate_invoice']);

/**
 * Turn a DRF 400 body into something an operator can act on.
 *
 * Why this exists: a nested serializer (`line_items`) returns a LIST OF DICTS,
 * one per submitted row. The screens used to render it with
 * `errors.join(', ')`, which stringifies each dict as "[object Object]" -- so
 * "Validation errors: line_items: [object Object]" was the entire message a
 * user got when a line price carried one decimal too many. The reason was in
 * the response the whole time; only the rendering threw it away.
 *
 * Row errors come back positionally, so index 0 is the first row the screen
 * submitted -- that is what makes "Line 3" trustworthy.
 *
 * @param {Error|Object} error - Axios error.
 * @param {string} fallback - Shown when the body carries nothing readable.
 * @returns {string} One line per problem, each naming the row and the field.
 */
const looksLikeMarkup = (text) => /^\s*</.test(text) || /<(html|body|head|title)[\s>]/i.test(text);

export function extractApiError(error, fallback = 'Something went wrong. Please try again.') {
  const data = composedFromServerAnswer(error) ? undefined : error?.response?.data;
  if (!data) return error?.message || fallback;
  // A string body is shown only when it is a sentence. A crashed server, a
  // proxy mid-deploy or an upload the web server refused answers with an HTML
  // page, and printing that markup as the error is worse than the fallback
  // (seen on the Paper Pricing Tasks card, ledger row 132).
  if (typeof data === 'string') return (looksLikeMarkup(data) ? '' : data.trim()) || fallback;
  if (typeof data !== 'object') return fallback;

  if (data.error) return asText(data.error) || fallback;
  if (data.message) return asText(data.message) || fallback;
  if (data.detail) return asText(data.detail) || fallback;
  if (data.non_field_errors) return asText(data.non_field_errors) || fallback;

  const lines = [];
  Object.entries(data).forEach(([field, errors]) => {
    // Structured hints a screen acts on, not sentences to read out. Rendering
    // them would append "Duplicate Invoice: 42, AP-00000014" to the message.
    if (METADATA_FIELDS.has(field)) return;

    const rowLabel = ROW_COLLECTIONS[field];
    const isRowList =
      rowLabel && Array.isArray(errors) && errors.some((e) => e && typeof e === 'object' && !Array.isArray(e));

    if (isRowList) {
      errors.forEach((rowErrors, idx) => {
        if (!rowErrors || typeof rowErrors !== 'object') return;
        const text = Array.isArray(rowErrors) ? asText(rowErrors) : fieldsToText(rowErrors, '; ');
        if (text) lines.push(`${rowLabel} ${idx + 1} — ${text}`);
      });
      return;
    }

    const text = asText(errors);
    if (text) lines.push(`${labelFor(field)}: ${text}`);
  });

  return lines.length ? lines.join('\n') : fallback;
}
